<template>
  <div class="process-designer" tabindex="0" ref="designerRef" @keydown.ctrl.z.prevent="undo" @keydown.ctrl.y.prevent="redo" @keydown.delete="deleteSelected">
    <!-- Menu Bar -->
    <div class="pd-menubar">
      <div class="menu-left">
        <button class="menu-btn" @click="goBack" title="返回"><i class="fas fa-arrow-left"></i></button>
        <button class="menu-btn" @click="saveFlow" title="保存" :disabled="saving"><i :class="saving ? 'fas fa-spinner fa-spin' : 'fas fa-save'"></i></button>
        <button class="menu-btn" @click="undo" :disabled="!canUndo" title="撤销"><i class="fas fa-undo"></i></button>
        <button class="menu-btn" @click="redo" :disabled="!canRedo" title="重做"><i class="fas fa-redo"></i></button>
        <span class="menu-sep"></span>
        <button class="menu-btn" title="剪切"><i class="fas fa-cut"></i></button>
        <button class="menu-btn" title="复制"><i class="fas fa-copy"></i></button>
        <button class="menu-btn" title="粘贴"><i class="fas fa-paste"></i></button>
        <span class="menu-sep"></span>
        <button class="menu-btn" @click="zoomIn" title="放大"><i class="fas fa-search-plus"></i></button>
        <button class="menu-btn" @click="zoomOut" title="缩小"><i class="fas fa-search-minus"></i></button>
        <button class="menu-btn" @click="fitAll" title="适应画布"><i class="fas fa-expand-arrows-alt"></i></button>
        <button class="menu-btn" title="清空画布"><i class="fas fa-trash"></i></button>
        <span class="menu-sep"></span>
        <button class="menu-btn" title="对齐方式"><i class="fas fa-align-left"></i></button>
        <button class="menu-btn" title="自动排列"><i class="fas fa-sort-amount-up"></i></button>
        <span class="menu-sep"></span>
        <button class="menu-btn" title="流程校验"><i class="fas fa-check-circle"></i></button>
        <button class="menu-btn" title="测试流程"><i class="fas fa-play-circle"></i></button>
      </div>
      <div class="menu-right">
        <span class="menu-label">对齐:</span>
        <input type="text" class="menu-input" :value="selectedNode?.x || 0" @change="updateNodePos('x', $event)" size="4" />
        <span class="menu-label">,</span>
        <input type="text" class="menu-input" :value="selectedNode?.y || 0" @change="updateNodePos('y', $event)" size="4" />
        <span class="menu-sep"></span>
        <span class="menu-label">缩放:</span>
        <select class="menu-select" :value="Math.round(scale * 100)" @change="updateScale($event)">
          <option :value="25">25%</option>
          <option :value="50">50%</option>
          <option :value="75">75%</option>
          <option :value="100">100%</option>
          <option :value="150">150%</option>
          <option :value="200">200%</option>
        </select>
      </div>
    </div>

    <!-- Body -->
    <div class="pd-body">
      <!-- Left: Node Palette -->
      <NodePalette @addNode="addNodeFromPalette" />

      <!-- Center: Canvas -->
      <div class="pd-center">
        <DesignerCanvas
          ref="canvasRef"
          :nodes="nodes"
          :edges="edges"
          :selected-id="selectedId"
          :selected-edge-id="selectedEdgeId"
          :connecting-from="connectingFrom"
          :scale="scale"
          @selectNode="selectNode"
          @clearSelection="clearSelection"
          @moveNode="moveNode"
          @addNode="addNodeAtPos"
          @startConnect="startConnect"
          @finishConnect="onFinishConnect"
          @cancelConnect="cancelConnect"
          @deleteSelected="deleteSelected"
          @selectEdge="selectEdge"
          @editNode="editNode"
          @editEdge="editEdge"
          @updateScale="onScaleChange"
          @updateWaypoints="onUpdateWaypoints"
        />
      </div>

      <!-- Right: Properties -->
      <NodeProperties
        v-if="selectedNode"
        :key="selectedNode.id"
        :node="selectedNode"
        :nodes="nodes"
        :edges="edges"
        @update="onPropUpdate"
        @delete="deleteNode"
        @close="clearSelection"
        @fieldsLoaded="onFieldsLoaded"
      />
      <EdgeProperties
        v-if="selectedEdge && !selectedNode"
        :edge="selectedEdge"
        :nodes="nodes"
        :table-groups="sharedTableGroups"
        @update="onPropUpdate"
        @delete="deleteEdge"
        @close="clearSelection"
      />
    </div>

    <!-- Status Bar -->
    <div class="pd-statusbar">
      <span class="status-left">节点数: {{ nodes.length }} | 连线数: {{ edges.length }}</span>
      <span class="status-right">流程设计器</span>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import NodePalette from './ProcessDesigner/NodePalette.vue'
import DesignerCanvas from './ProcessDesigner/DesignerCanvas.vue'
import NodeProperties from './ProcessDesigner/NodeProperties.vue'
import EdgeProperties from './ProcessDesigner/EdgeProperties.vue'
import { useFlowState, nodeTypes, syncNid } from './ProcessDesigner/flowState.js'
import { getFlowDesignParsed, saveFlowDesignFull } from '../../api/process'

const state = useFlowState()
const { nodes, edges, selectedId, selectedNode, selectedEdgeId, selectedEdge, connectingFrom,
  addNode, removeNode, moveNode, selectNode, clearSelection,
  addEdge, removeEdge, startConnect, finishConnect, updateNodeProp,
  selectEdge, clearEdgeSelection, updateEdgeProp,
  getSnapshot, restoreSnapshot, defaultEdgeProps
} = state

const canvasRef = ref(null)
const scale = ref(1)
const processGuid = ref('')
const saving = ref(false)
const sharedTableGroups = ref([])

const processName = computed(() => {
  const p = window.currentEditingProcess
  return p ? p.name : '新建流程'
})

// Undo/Redo
const history = ref([])
const histIdx = ref(-1)

function pushHistory() {
  histIdx.value++
  history.value = history.value.slice(0, histIdx.value)
  history.value.push(getSnapshot())
  if (history.value.length > 50) { history.value.shift(); histIdx.value-- }
}

const canUndo = computed(() => histIdx.value > 0)
const canRedo = computed(() => histIdx.value < history.value.length - 1)

function undo() {
  if (!canUndo.value) return
  histIdx.value--
  restoreSnapshot(history.value[histIdx.value])
}
function redo() {
  if (!canRedo.value) return
  histIdx.value++
  restoreSnapshot(history.value[histIdx.value])
}

function addNodeFromPalette(key) {
  addNode(key, 300, 150 + nodes.value.length * 80)
  pushHistory()
}

function addNodeAtPos(key, x, y) {
  addNode(key, x, y)
  pushHistory()
}
function onFinishConnect(toId) {
  finishConnect(toId)
  pushHistory()
}

function cancelConnect() {} // 占位，实际由 flowState 处理

function deleteNode(id) { removeNode(id); pushHistory() }
function deleteEdge(id) { removeEdge(id); pushHistory() }
function deleteSelected() {
  if (selectedId.value) { deleteNode(selectedId.value) }
  else if (selectedEdgeId.value) { deleteEdge(selectedEdgeId.value) }
  pushHistory()
}
function onPropUpdate() { pushHistory() }
function onFieldsLoaded(groups) { sharedTableGroups.value = groups }

function onUpdateWaypoints(edgeId, waypoints) {
  const edge = edges.value.find(e => e.id === edgeId)
  if (edge) { edge.waypoints = waypoints }
  pushHistory()
}

// ===== 保存流程设计 =====
async function saveFlow() {
  if (!processGuid.value) { window.$message.info('流程信息缺失，无法保存'); return }
  saving.value = true
  try {
    // 收集待上传的文件并上传
    for (const n of nodes.value) {
      if (n.props._operateDocFile instanceof File) {
        n.props.operateDoc = n.props._operateDocFile.name
        n.props.operateDocName = n.props._operateDocFile.name
        delete n.props._operateDocFile
      }
    }
    // 构造流程 XML 数据（用于 mxGraph 兼容）
    const flowData = JSON.stringify({
      nodes: nodes.value.map(n => ({
        id: n.id, nodeType: n.nodeType, typeKey: n.typeKey,
        name: n.name, x: n.x, y: n.y, props: n.props
      })),
      edges: edges.value.map(e => ({
        id: e.id, from: e.from, to: e.to, label: e.label || '',
        waypoints: e.waypoints || []
      }))
    })

    // 构造完整的流程设计数据
    const design = {
      nodes: nodes.value.map(n => ({
        id: n.id, nodeType: n.nodeType, typeKey: n.typeKey,
        name: n.name, x: n.x, y: n.y, width: n.width || 70, height: n.height || 62,
        props: n.props
      })),
      edges: edges.value.map(e => ({
        id: e.id, source: e.from, target: e.to, label: e.label || '', props: e.props || {},
        waypoints: e.waypoints || []
      }))
    }

    const res = await saveFlowDesignFull({
      FlowGuid: processGuid.value,
      FlowData: flowData,
      Design: design,
      Updater: 'system'
    })
    const data = res.Data !== undefined ? res : (res.data || res)
    if (data.resultType !== undefined ? data.resultType : data.ResultType) {
      window.$message.success('流程保存成功！')
    } else {
      window.$message.error('保存失败: ' + (data.msg || data.Msg || '未知错误'))
    }
  } catch (e) {
    window.$message.error('保存失败: ' + (e.message || e))
  } finally {
    saving.value = false
  }
}

// ===== 加载流程设计（新 API：自动解析 mxGraph XML + DB 节点表）=====
async function loadFlowDesign() {
  const p = window.currentEditingProcess
  if (!p || !p.guid) {
    console.warn('[loadFlowDesign] 无流程信息')
    return false
  }
  processGuid.value = p.guid
  try {
    const res = await getFlowDesignParsed(p.guid)
    // 后端序列化：resultType/data 可能是小写，Nodes/nodes 都可能出现
    const resultData = res.data ?? res.Data ?? null
    const nodeList = resultData?.nodes ?? resultData?.Nodes ?? null
    const edgeList = resultData?.edges ?? resultData?.Edges ?? null
    if (!nodeList || nodeList.length === 0) {
      console.warn('[loadFlowDesign] API 返回无节点数据, res:', res)
      return false
    }

    // 直接加载解析后的数据（字段可能是 PascalCase 或 camelCase）
    nodes.value = nodeList.map(n => ({
      id: (n.id ?? n.Id),
      nodeType: (n.nodeType ?? n.NodeType),
      typeKey: (n.typeKey ?? n.TypeKey),
      name: (n.name ?? n.Name ?? ''),
      shape: nodeTypes[n.typeKey ?? n.TypeKey]?.shape || 'rect',
      x: (n.x ?? n.X ?? 100),
      y: (n.y ?? n.Y ?? 100),
      width: (n.width ?? n.Width ?? 70),
      height: (n.height ?? n.Height ?? 62),
      props: (n.props ?? n.Props ?? {})
    }))
    syncNid(nodes.value)
    edges.value = (edgeList || []).map(e => {
      const ep = (e.props ?? e.Props)
      return {
        id: e.id ?? e.Id,
        from: e.source ?? e.Source ?? e.from,
        to: e.target ?? e.Target ?? e.to,
        label: e.label ?? e.Label ?? '',
        waypoints: e.waypoints ?? e.Waypoints ?? [],
        props: ep ? { ...defaultEdgeProps(), ...(typeof ep === 'object' ? ep : {}) } : defaultEdgeProps()
      }
    })
    // 调试日志：输出每个节点的关键属性
    nodes.value.forEach(n => {
      const cnl = n.props?.customNotifyList
      const el = n.props?.eventList
      console.log(`[loadFlowDesign] Node ${n.id}: type=${n.nodeType}, taskNotify='${n.props?.taskNotify}', notifyChannels='${n.props?.notifyChannels}', ccList=${n.props?.ccList?.length || 0}, eventList=${el?.length || 0}, customNotifyList=${cnl?.length || 0}`)
      if (cnl?.length > 0) console.log(`[loadFlowDesign]   customNotifyList[0]:`, cnl[0])
      if (el?.length > 0) console.log(`[loadFlowDesign]   eventList[0]:`, el[0])
    })
    return true
  } catch (e) {
    console.error('[loadFlowDesign] 异常:', e)
    return false
  }
}

// 新建空白流程
function addDefaultFlow() {
  nodes.value = []
  edges.value = []
  addNode('start', 50, 150)
  addNode('approval', 180, 150)
  addNode('approval', 310, 150)
  addNode('approval', 440, 150)
  addNode('sqlplugin', 570, 150)
  addNode('approval', 700, 150)
  addNode('approval', 830, 150)
  addNode('approval', 960, 150)
  addNode('notify', 1090, 150)
  addNode('end', 1220, 150)
  addEdge(nodes.value[0].id, nodes.value[1].id)
  addEdge(nodes.value[1].id, nodes.value[2].id)
  addEdge(nodes.value[2].id, nodes.value[3].id)
  addEdge(nodes.value[3].id, nodes.value[4].id)
  addEdge(nodes.value[4].id, nodes.value[5].id)
  addEdge(nodes.value[5].id, nodes.value[6].id)
  addEdge(nodes.value[6].id, nodes.value[7].id)
  addEdge(nodes.value[7].id, nodes.value[8].id)
  addEdge(nodes.value[8].id, nodes.value[9].id)
  pushHistory()
}

function zoomIn() {
  scale.value = Math.min(2, scale.value * 1.25)
}

function zoomOut() {
  scale.value = Math.max(0.25, scale.value / 1.25)
}

function fitAll() {
  scale.value = 1
}

function updateScale(e) {
  scale.value = parseInt(e.target.value) / 100
}

function updateNodePos(axis, e) {
  if (selectedNode.value) {
    moveNode(selectedId.value,
      axis === 'x' ? parseInt(e.target.value) || 0 : selectedNode.value.x,
      axis === 'y' ? parseInt(e.target.value) || 0 : selectedNode.value.y
    )
  }
}

function onScaleChange(newScale) {
  scale.value = newScale
}

function goBack() {
  window.dispatchEvent(new Event('navigateToProcessManagement'))
}

function editNode() { /* double-click to open properties — handled via NodeProperties visibility */ }
function editEdge(edge) { selectEdge(edge.id); /* opens EdgeProperties */ }

onMounted(async () => {
  console.log('[ProcessDesigner] mounted, currentEditingProcess:', window.currentEditingProcess)
  const loaded = await loadFlowDesign()
  if (!loaded) {
    console.warn('[ProcessDesigner] 未加载到流程数据，使用默认空白设计')
    addDefaultFlow()
  }
})
</script>

<style scoped>
.process-designer { display: flex; flex-direction: column; height: calc(100vh - 110px); background: #f9fafb; outline: none; border: 1px solid #e5e7eb; }

/* Menu Bar */
.pd-menubar { display: flex; justify-content: space-between; align-items: center; padding: 4px 8px; height: 28px; background: #f5f6f8; border-bottom: 1px solid #e5e7eb; }
.pd-menubar .menu-left, .pd-menubar .menu-right { display: flex; align-items: center; gap: 2px; }
.menu-btn { width: 24px; height: 24px; border: 1px solid #d4d6db; border-radius: 3px; background: #fff; cursor: pointer; display: flex; align-items: center; justify-content: center; font-size: 12px; color: #6b7280; }
.menu-btn:hover { background: #e5e7eb; border-color: #9ca3af; color: #374151; }
.menu-btn:disabled { opacity: 0.35; cursor: not-allowed; }
.menu-sep { width: 1px; height: 16px; background: #d4d6db; margin: 0 4px; }
.menu-label { font-size: 11px; color: #6b7280; }
.menu-input { width: 45px; height: 18px; border: 1px solid #d4d6db; border-radius: 3px; font-size: 11px; text-align: center; background: #fff; color: #374151; }
.menu-input:focus { outline: none; border-color: #10b981; }
.menu-select { height: 20px; border: 1px solid #d4d6db; border-radius: 3px; font-size: 11px; background: #fff; color: #374151; }

/* Body */
.pd-body { display: flex; flex: 1; overflow: hidden; }
.pd-center { flex: 1; overflow: hidden; }

/* Status Bar */
.pd-statusbar { display: flex; justify-content: space-between; padding: 2px 10px; height: 22px; background: #f5f6f8; border-top: 1px solid #e5e7eb; font-size: 11px; color: #6b7280; }
</style>
